// app/src/lib/theme.ts — light/dark/system theme with persistence.
// The resolved theme is written to <html data-theme="light|dark"> so CSS only
// needs [data-theme="dark"] overrides (no media-query duplication). An inline
// boot script in index.html applies the same logic before paint to avoid FOUC.

export type ThemePref = "light" | "dark" | "system";
const KEY = "epos.theme";

export function getThemePref(): ThemePref {
  const v = localStorage.getItem(KEY);
  return v === "light" || v === "dark" || v === "system" ? v : "dark";
}

function systemPrefersDark(): boolean {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
}

/** The concrete theme that a preference resolves to right now. */
export function resolveTheme(pref: ThemePref = getThemePref()): "light" | "dark" {
  return pref === "system" ? (systemPrefersDark() ? "dark" : "light") : pref;
}

/** Apply a preference: set <html data-theme> and sync the browser theme-color. */
export function applyTheme(pref: ThemePref = getThemePref()): "light" | "dark" {
  const resolved = resolveTheme(pref);
  document.documentElement.dataset.theme = resolved;
  document.documentElement.dataset.bsTheme = resolved; // Tabler/Bootstrap colour mode
  // keep the address-bar / standalone chrome colour in step with the canvas
  const colour = resolved === "dark" ? "#0A0A0A" : "#F4EFE7";
  document.querySelectorAll('meta[name="theme-color"]').forEach((m) => {
    if (!m.getAttribute("media")) m.setAttribute("content", colour);
  });
  return resolved;
}

/** Persist a new preference and apply it. */
export function setThemePref(pref: ThemePref): "light" | "dark" {
  localStorage.setItem(KEY, pref);
  return applyTheme(pref);
}

/** Cycle light -> dark -> system -> light (used by the toggle button). */
export function cycleTheme(): ThemePref {
  const order: ThemePref[] = ["light", "dark", "system"];
  const next = order[(order.indexOf(getThemePref()) + 1) % order.length];
  setThemePref(next);
  return next;
}

/** One-time: adopt dark as the baseline on devices not yet migrated, so the app
 *  opens black by default. Guarded by a flag so it never overrides later choices. */
function migrateDarkDefault(): void {
  try {
    if (!localStorage.getItem("epos.theme.default-v2")) {
      localStorage.setItem(KEY, "dark");
      localStorage.setItem("epos.theme.default-v2", "1");
    }
  } catch { /* ignore */ }
}

/** Call once on boot: apply stored pref and react to OS changes when on 'system'. */
export function initTheme(): void {
  migrateDarkDefault();
  applyTheme();
  window.matchMedia?.("(prefers-color-scheme: dark)").addEventListener?.("change", () => {
    if (getThemePref() === "system") applyTheme("system");
  });
}
